import PropTypes from 'prop-types'
import React, { useCallback, useContext, useEffect, useMemo, useReducer, useState } from 'react'
import Helmet from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { throttle, upperCase } from 'lodash'
import classnames from 'classnames'
import { ErrorBoundary } from 'react-error-boundary'
import moment from 'moment'

import Layout from '../components/Layout'
import { DEFAULT_EVENT_FILTERS, EventFilterContext, EventFilterDispatchContext } from '../components/Events/Filters'

import { initStateArray, arrayReducer, initStateObject, objectReducer, STALE, OVERWRITE } from '../reducers/default'
import {
    changeFbCampaignStatus,
    changeOptimizationStatus,
    fetchEvent,
    fetchEvents,
} from '../api/events'
import SideDrawer from '../components/Shared/SideDrawer'
import LoadingSpinner, { SpinnerSize } from '../components/Shared/LoadingSpinner'
import { hasRestrictedAccessFor } from '../utils/helpers'
import { useSelector } from 'react-redux'
import EventList from '../components/Event/List/EventList'
import EventRow from '../components/Event/List/EventRow'
import EventHeader from '../components/Event/Details/EventHeader'
import { EventCampaigns } from '../components/Recommendations/EventCampaigns'
import CampaignSetupProvider from '../components/Recommendations/Campaign/Edit/CampaignSetupProvider'
import AutosaveStatus from '../components/Recommendations/Campaign/Edit/AutosaveStatus'

import ErrorFallback from '../components/Shared/ErrorFallback'
import ErrorMessage from '../components/Shared/ErrorMessage'
import { EventCampaignStatus, getEventFetchFilters } from '../api/models'
import EventControlPanel from 'components/Events/EventControlPanel'
import PortalModal from 'components/Shared/PortalModal'
import CampaignActivationModal from 'components/Recommendations/CampaignStatusModals/CampaignActivationModal'
import CampaignOptimizationModal from 'components/Recommendations/CampaignStatusModals/CampaignOptimizationModal'
import { EventStatsProvider } from 'components/Event/Details/EventStatsProvider'

const pageInit = { number: 1, fetched: 1, loading: false }

/**
 * Recommendations - page template.
 * It displays tabs with active, open, and ignored events in the left sidebar and chosen event campaigns in the right column.
 * @Tags( page, layout, recommendations, campaigns, logged-users, api )
 * @SuggestedTags( recommendations )
 * @Endpoints( /events/ )
 * @ApiLogic( ../api/events )
 * @Routes( /login )
 */
const RecommendationsContent = () => {
    const { t } = useTranslation()
    const user = useSelector(store => store.user)
    const [page, setPage] = useState({ ...pageInit })
    const [isCampaignActive, setIsCampaignActive] = useState(false)
    const [isCampaignOptimized, setIsCampaignOptimized] = useState(false)
    const [isActivationModalOpen, setIsActivationModalOpen] = useState(false)
    const [isOptimizationModalOpen, setIsOptimizationModalOpen] = useState(false)
    const [events, dispatch] = useReducer(arrayReducer, initStateArray)
    const [selectedEvent, eventDispatch] = useReducer(objectReducer, initStateObject)
    const [drawerOpen, setDrawerOpen] = useState(false)
    const filters = useContext(EventFilterContext)
    const dispatchFiltersAction = useContext(EventFilterDispatchContext)
    const [campaignStatus, statusDispatch] = useReducer(objectReducer, initStateObject)
    const [campaignOptimized, optimizationDispatch] = useReducer(objectReducer, initStateObject)
    const isMenuShown = useMemo(() => {
        if(selectedEvent.content?.client_campaign_status)
            return Object.values(EventCampaignStatus).includes(upperCase(selectedEvent.content?.client_campaign_status))
        return selectedEvent.content?.has_active_campaigns
    }, [selectedEvent.content])
    const isPastEvent = useMemo(() => moment(selectedEvent.content?.date) < moment(), [selectedEvent.content])

    // FIXME: Event upload and it's success modal were removed from searchFilter, need to be added here. Refactor out the relevant components in pages/Events.js and reuse the resulting component here.

    useEffect(() => {
        if (!drawerOpen) {
            eventDispatch({ type: STALE })
            if (!selectedEvent.loading)
                eventDispatch({ type: OVERWRITE, payload: initStateObject })

            return
        }

        const urlParams = new URL(window.location.href)
        const eventId = urlParams.searchParams.get('event-id')
        if (selectedEvent.content.id === eventId) return

        eventId && fetchEvent(eventDispatch, eventId)
    }, [drawerOpen, selectedEvent.loading, selectedEvent.content.id])

    const loadNextPage = useCallback(() => {
        if (page.loading) return

        const pageNumber = (page.number + 1)
        setPage({ number: pageNumber, loading: true })

        fetchEvents((action) => {
            dispatch(action)
            setPage(page => ({ ...page, loading: false }))
        }, getEventFetchFilters(filters, pageNumber))
    }, [page, filters])

    useEffect(() => {
        const handleScroll = () => {
            if (
                events.maxPage > page.number &&
                    !page.loading &&
                    document.body.scrollHeight - (window.innerHeight + window.scrollY) < 300
            ) {
                loadNextPage()
            }
        }
        const throttledScroll = throttle(handleScroll, 1000)

        window.addEventListener('scroll', throttledScroll)
        return () => window.removeEventListener('scroll', throttledScroll)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [events, page])

    useEffect(() => {
        if(campaignStatus.error)
            setIsCampaignActive(false)
        if(campaignOptimized.error)
            setIsCampaignOptimized(false)
    }, [campaignOptimized, campaignStatus])

    useEffect(() => {
        if(selectedEvent.content) {
            const isCampaignActive = upperCase(selectedEvent.content?.client_campaign_status) === EventCampaignStatus.active || 
                (selectedEvent.content?.client_campaign_status === null && selectedEvent.content?.has_active_campaigns)
            const isCampaignOptimized = upperCase(selectedEvent.content?.campaign_optimisation_status) !== EventCampaignStatus.paused
            setIsCampaignActive(isCampaignActive)
            setIsCampaignOptimized(isCampaignOptimized)
        }
    }, [selectedEvent.content])

    const filterEvents = useCallback((eventFilters) => {
        if (!eventFilters) return

        setPage({ ...pageInit })
        fetchEvents(dispatch, getEventFetchFilters(eventFilters))
        dispatchFiltersAction({ type: 'SET_OPEN', payload: false })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const resetFilters = (includeAll = true) => {
        if(includeAll) {
            dispatchFiltersAction({ type: 'CLEAR_ALL' })
            filterEvents({ ...DEFAULT_EVENT_FILTERS })
        } else {
            dispatchFiltersAction({ type: 'CLEAR_ALL_BUT_PAST' })
            filterEvents({ ...DEFAULT_EVENT_FILTERS, includePastEvents: filters?.includePastEvents })
        }
    }

    useEffect(() => {
        filterEvents(filters)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    
    const menuOptions = [
        {
            title: isPastEvent ? t('Recommendations.campaignOptions.statuses.inactive') : 
                (isCampaignActive ? t('Recommendations.campaignOptions.statuses.active') : 
                    t('Recommendations.campaignOptions.statuses.paused')),
            optionType: 'text',
            className: 'bottom-border no-hover',
            disabled: isPastEvent
        },
        {
            title: t('Recommendations.campaignOptions.activateCampaign'),
            optionType: 'switch',
            onClick: () => setIsActivationModalOpen(true),
            checked: isCampaignActive,
            disabled: isPastEvent
        }
    ]

    if(isCampaignActive) {
        menuOptions.push({
            title: t('Recommendations.campaignOptions.optimizeCampaign'),
            optionType: 'switch',
            onClick: () => setIsOptimizationModalOpen(true),
            checked: isCampaignOptimized,
            disabled: isPastEvent
        })
    }

    return (
        <Layout loggedIn
            title={t('Recommendations.PageTitle')}
            mobileTopBar={{ centerTitle: t('Recommendations.MobileTitle') }}
        >
            <Helmet bodyAttributes={{ class: 'events-page' }}/>
            <main>
                <div data-testid='campaigns-page-wrapper' style={{ minHeight: '100vh', paddingBottom: '8em' }} className="container-fluid hide-scroll-bar">
                    <div className="col-12 pl-0">
                        <h1 className="main-title hide-mobile">
                            {t('Recommendations.MainTitle')}
                        </h1>
                    </div>
                    <div className='mt-4'>
                        <EventControlPanel
                            filters={filters}
                            onDispatch={dispatchFiltersAction}
                            onSubmit={filterEvents}
                            onReset={(includeAll) => resetFilters(includeAll)}
                        />
                    </div>
                    <div
                        className={classnames({
                            'events-page-search-filter_table card mt-3': true,
                            'events-page-search-filter_table--filtering': filters.open
                        })}
                        data-testid="events-search-table"
                    >
                        {drawerOpen && selectedEvent.content.id && events.items.every(x => x.id !== selectedEvent.content.id) &&
                            <EventRow
                                event={selectedEvent.content}
                                key={selectedEvent.content.id}
                                isSelected
                                isDisabled={hasRestrictedAccessFor(user.accessLevel, 'pages', 'demandPredictionSingle')}
                                classNames="hover"
                            />
                        }
                        <div data-testid='events-search-table-results' className='events-page-search-filter_results_container'>
                            {events.items.length > 0 ?
                                <EventList
                                    events={events.items}
                                    rowRender={(event) =>
                                        <EventRow
                                            event={event}
                                            key={event.id}
                                            onClick={(eventId) => {
                                                eventId && fetchEvent(eventDispatch, eventId)
                                            }}
                                            shouldScrollOnLoad
                                            isSelected={selectedEvent.content.id === event.id}
                                            isDisabled={hasRestrictedAccessFor(user.accessLevel, 'pages', 'salesBoostSingle')}
                                            classNames="hover"
                                        />
                                    }
                                />
                                : null
                            }
                        </div>
                        {!events.loading && !events.items.length > 0 ?
                            <p style={{ width: '100%', textAlign: 'center', marginTop: '1em', marginBottom: '1em' }}>
                                {t('Events.results.empty')}
                            </p>
                            : null
                        }
                    </div>
                    {events.loading ? <LoadingSpinner portalBackground={false} size={SpinnerSize.LARGE} /> : null}
                    <SideDrawer
                        drawerOpen={drawerOpen}
                        toggle={setDrawerOpen}
                        classes="no-padding"
                    >
                        {selectedEvent.error &&
                            <ErrorMessage classNames="mt-6" danger>{t(selectedEvent.message)}</ErrorMessage>
                        }
                        {selectedEvent.success && selectedEvent.content.id && !selectedEvent.stale &&
                            <ErrorBoundary FallbackComponent={ErrorFallback}>
                                <EventStatsProvider eventId={selectedEvent.content.id}>
                                    <CampaignSetupProvider eventId={selectedEvent.content.id}>
                                        <>
                                            <EventHeader
                                                menuOptions={menuOptions}
                                                classes='event-header_recommendation-drawer-padding'
                                                event={selectedEvent.content}
                                                showActiveBadge={false}
                                                showMenu={isMenuShown}
                                            >
                                                <AutosaveStatus />
                                            </EventHeader>
                                            {campaignStatus.error &&
                                            <ErrorMessage classNames="recommendations-page_error" danger>{t(campaignStatus.message)}</ErrorMessage>
                                            }
                                            {campaignOptimized.error &&
                                            <ErrorMessage classNames="recommendations-page_error" danger>{t(campaignOptimized.message)}</ErrorMessage>
                                            }
                                            <EventCampaigns eventId={selectedEvent.content.id} />
                                            <PortalModal isOpen={isActivationModalOpen}>
                                                <CampaignActivationModal
                                                    status={isCampaignActive ? 'pause' : 'activate'} 
                                                    onCancel={() => setIsActivationModalOpen(false)} 
                                                    onConfirm={() => {
                                                        setIsActivationModalOpen(false)
                                                        const status = isCampaignActive ? EventCampaignStatus.paused : EventCampaignStatus.active
                                                        changeFbCampaignStatus(statusDispatch, selectedEvent.content.id, status)
                                                        setIsCampaignActive(prev => !prev)
                                                    }}
                                                />
                                            </PortalModal>
                                            <PortalModal isOpen={isOptimizationModalOpen}>
                                                <CampaignOptimizationModal
                                                    status={isCampaignOptimized ? 'deactivate' : 'activate'} 
                                                    onCancel={() => setIsOptimizationModalOpen(false)} 
                                                    onConfirm={() => {
                                                        setIsOptimizationModalOpen(false)
                                                        const status = isCampaignOptimized ? EventCampaignStatus.paused : EventCampaignStatus.active
                                                        changeOptimizationStatus(optimizationDispatch, selectedEvent.content.id, status)
                                                        setIsCampaignOptimized(prev => !prev)
                                                    }}
                                                />
                                            </PortalModal>
                                        </>
                                    </CampaignSetupProvider>
                                </EventStatsProvider>
                            </ErrorBoundary>
                        }
                    </SideDrawer>
                </div>
            </main>
        </Layout>
    )
}

RecommendationsContent.propTypes = {
    /**
   * Link match with optional event ID
   */
    match: PropTypes.object
}

export default function Recommendations() {
    return (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
            <RecommendationsContent />
        </ErrorBoundary>
    )
}

// Recommendations.whyDidYouRender = true
